import {Component, Inject, Optional, AfterViewInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {S3ImageBlob, S3UploaderRatioEnum, S3UploaderRatios} from '../uploader/s3-uploader.types';
import {ImageCroppedEvent} from 'ngx-image-cropper/lib/interfaces/image-cropped-event.interface';
import {CropperPosition, ImageTransform} from 'ngx-image-cropper';
import {SnackService} from '../../../services/snack/snack.service';
import {TranslateService} from '@ngx-translate/core';
import {cloneDeep} from 'lodash-es';
import {Dimensions} from 'ngx-image-cropper/lib/interfaces';
import {UtilService} from "../../../services/util/util.service";

@Component({
  selector: 'app-s3-image-editor',
  templateUrl: './s3-image-editor.component.html',
  styleUrls: ['./s3-image-editor.component.scss']
})

export class S3ImageEditorComponent implements AfterViewInit {
  public imageBlob: S3ImageBlob;
  public loadedImage: string | ArrayBuffer;
  public originalRatio: S3UploaderRatioEnum;
  public cropperRatio: number = 16 / 9;
  public cropperMaxWidth: number;
  public cropperMaxHeight: number;
  public cropperMinWidth: number = 0;
  public cropperMinHeight: number = 0;
  public resizeToWidth: number;
  public cropperScale: number = 1;
  public maintainAspectRatio: boolean = true;
  public containWithinAspectRatio: boolean = false;
  public resetCropOnAspectRatioChange: boolean;
  public imagesOutput: S3ImageBlob = {};
  public cropperPositionsSaved: {[key: string]: { position: CropperPosition, scale: number }} = {};
  public transform: ImageTransform = {};
  public cropperPosition: CropperPosition = { x1: 0, x2: 0, y1: 0, y2: 0 };
  public maxSize: Dimensions;
  public thumbSize: Dimensions;
  public thumbRatio: S3UploaderRatioEnum;
  public thumbCropperIsBusy: boolean;
  public cropperIsBusy: boolean;
  public thumbCropperQueue: S3UploaderRatioEnum[] = [];
  public thumbSource: string | ArrayBuffer;
  public multipleRatios: boolean;
  public S3UploaderRatioValues: S3UploaderRatioEnum[] = [S3UploaderRatios.Modern, S3UploaderRatios.Retro, S3UploaderRatios.Square];
  public resize: boolean;
  public usePrimaryImage: boolean = true;
  public isLoading: boolean;
  public activeTabIndex: number = 0;

  public isChanged: boolean;
  private _origCropSize: Record<string, {changed: boolean, pos: string}> = {
    '1x1': null,
    '4x3': null,
    '16x9': null,
    'logo': null,
    'email': null
  };

  constructor(public dialogRef: MatDialogRef<S3ImageEditorComponent>,
              private translateService: TranslateService,
              private snackService: SnackService,
              public utilService: UtilService,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: any) {
    if (data.imageBlob) this.imageBlob = data.imageBlob;
    if (data.multipleRatios) this.multipleRatios = true;
    if (data.ratio) {
      this.originalRatio = data.ratio;
      this.switchRatio(data.ratio);
    }
    if (data.resize) this.resize = true;
  }

  public ngAfterViewInit(): void {
    if (this.resize && this.multipleRatios) {
      Object.keys(this.imageBlob).forEach(ratio => {
        this.imagesOutput[ratio] = this.imageBlob[ratio];
        this.createThumb(ratio as S3UploaderRatioEnum);
      });
    }
  }

  get ratioEnum(): S3UploaderRatioEnum {
    switch (this.cropperRatio) {
      case 1:
        return S3UploaderRatios.Square;
      case 4 / 3:
        return S3UploaderRatios.Retro;
      case 240 / 95:
        return S3UploaderRatios.Logo;
      case 1036 / 445:
        return S3UploaderRatios.Email;
      case 16 / 9: default:
        return S3UploaderRatios.Modern;
    }
  }

  public isRatioReady(ratio: S3UploaderRatioEnum): boolean {
    return this.imagesOutput && !!this.imagesOutput[ratio] && !!this.imagesOutput[ratio + '_thumb'];
  }

  get isValid(): boolean {
    return !this.multipleRatios || (Object.keys(this.imagesOutput).length === this.S3UploaderRatioValues.length * 2);
  }

  public getThumbSize(ratio: S3UploaderRatioEnum): Dimensions {
    switch (ratio) {
      case S3UploaderRatios.Modern:
        return {width: 320, height: 180};
      case S3UploaderRatios.Retro:
        return {width: 200, height: 150};
      case S3UploaderRatios.Square:
        return {width: 200, height: 200};
      case S3UploaderRatios.Email:
        return {width: 1036, height: 445};
    }
    return null;
  }

  public readBlob(blob: Blob) {
    this.isLoading = true;
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = (): void => {
      this.loadedImage = reader.result;
    };
  }

  public readThumbBlob(blob: Blob) {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = (): void => {
      this.thumbSource = reader.result;
    };
  }

  public switchRatio(ratio: S3UploaderRatioEnum): void {
    this.updateCropperSavedPositions();
    switch (ratio) {
      case S3UploaderRatios.Modern:
        this.cropperRatio = 16 / 9;
        this.resizeToWidth = 1920;
        this.maxSize = { height: 1920, width: 1080 };
        break;
      case S3UploaderRatios.Square:
        this.cropperRatio = 1;
        this.resizeToWidth = 800;
        this.maxSize = { height: 800, width: 800 };
        break;
      case S3UploaderRatios.Retro:
        this.cropperRatio = 4 / 3;
        this.resizeToWidth = 1024;
        this.maxSize = { height: 1024, width: 768 };
        break;
      case S3UploaderRatios.Logo:
        this.containWithinAspectRatio = true;
        this.cropperRatio = 240 / 95;
        this.resizeToWidth = 240;
        this.maxSize = { height: 240, width: 95 };
        break;
      case S3UploaderRatios.Email:
        this.containWithinAspectRatio = true;
        this.cropperRatio = 1036 / 445;
        this.resizeToWidth = 1036;
        this.maxSize = { height: 445, width: 1036 };
        break;
    }
    if (!this.usePrimaryImage) {
      this.readBlob(this.imageBlob[ratio]);
    } else if (!this.loadedImage) {
      this.readBlob(this.imageBlob[this.originalRatio]);
    }
    if (this.cropperPositionsSaved[ratio]) {
      setTimeout(() => {
        this.cropperPosition = this.cropperPositionsSaved[ratio].position;
        this.cropperScale = this.cropperPositionsSaved[ratio].scale;
        this.transform = {
          ...this.transform,
          scale: this.cropperScale
        };
      }, 0);
    } else {
      this.resetImage();
    }
  }

  public switchTab(tabIndex: number): void {
    const ratio = this.S3UploaderRatioValues[tabIndex];
    if (ratio) {
      this.switchRatio(ratio);
    }
  }

  private updateCropperSavedPositions(): void {
    this.cropperPositionsSaved[this.ratioEnum] = { position: cloneDeep(this.cropperPosition), scale: cloneDeep(this.cropperScale) };
  }

  public close(): void {
    this.dialogRef.close();
  }

  public save(): void {
    this.dialogRef.close(this.imagesOutput);
  }

  public saveOnlyCurrent(): void {
    Object.keys(this.imageBlob).forEach(ratio => {
      if (ratio !== this.ratioEnum) {
        this.imagesOutput[ratio] = this.imageBlob[ratio];
      }
    });
    this.dialogRef.close(this.imagesOutput);
  }

  public imageCropped($event: ImageCroppedEvent): void {
    this.isChanged = this.checkForChanges();
    if (!this.cropperIsBusy) {
      this.cropperIsBusy = true;
      this.imagesOutput[this.ratioEnum] = $event.blob;
      this.createThumb(this.ratioEnum);
    }
  }

  public createThumb(ratio: S3UploaderRatioEnum): void {
    if (ratio !== S3UploaderRatios.Logo) {
      if (!this.thumbCropperIsBusy) {
        this.thumbCropperIsBusy = true;
        this.thumbRatio = ratio;
        this.thumbSize = this.getThumbSize(ratio);
        this.readThumbBlob(this.imagesOutput[ratio]);
      } else {
        this.thumbCropperQueue.push(ratio);
      }
    }
  }

  public thumbCropped($event: ImageCroppedEvent): void {
    this.imagesOutput[this.thumbRatio + '_thumb'] = $event.blob;
    this.thumbCropperIsBusy = false;
    this.cropperIsBusy = false;
    this.thumbSource = '';
    if (this.thumbCropperQueue.length) {
      const next = this.thumbCropperQueue.shift();
      this.createThumb(next);
    }
  }

  public imageLoadFailed(): void {
    this.snackService.showError(this.translateService.instant('general.api_image_editor_unexpected_error'))
    this.close();
  }

  public resetImage(): void {
    this.cropperScale = 1;
    this.transform = {};
    this.updateCropperSavedPositions();
  }

  public zoomOut(): void {
    this.cropperScale -= .2;
    this.transform = {
      ...this.transform,
      scale: this.cropperScale
    };
    this.updateCropperSavedPositions();
  }

  public zoomIn(): void {
    this.cropperScale += .2;
    this.transform = {
      ...this.transform,
      scale: this.cropperScale
    };
    this.updateCropperSavedPositions();
  }

  public checkForChanges(): boolean {
    if (!this._origCropSize[this.ratioEnum]) {
      this._origCropSize[this.ratioEnum] = {
        changed: false,
        pos: JSON.stringify(this.cropperPosition)
      }
    }
    if (this._origCropSize[this.ratioEnum]?.pos !== JSON.stringify(this.cropperPosition)) {
      this._origCropSize[this.ratioEnum].changed = true;
      return true;
    } else if (this._origCropSize[this.ratioEnum]?.pos === JSON.stringify(this.cropperPosition)) {
      this._origCropSize[this.ratioEnum].changed = false;
      return Object.values(this.cropperPositionsSaved).some(value => value.scale !== 1) ||
        Object.values(this._origCropSize).some(value => value?.changed);
    }
    return true;
  }
}
